import type { FiscalShiftAction } from '@/entities/fiscalization';
import type { KkmExecutionResult, KkmHandshakeReady, KkmServerAdapter } from '@/shared/api';
import type { ConfirmShiftKktInput } from '@shared/api/graphql/gql/graphql';

import { parseKkmShiftSessionNumber } from '@/shared/api';

type FiscalShiftAdapter = Pick<KkmServerAdapter, 'closeShift' | 'handshake' | 'openShift'>;

export interface FiscalShiftActionOptions {
  /** Уже выполненный handshake: восстановление успевает проверить кассу до вызова. */
  handshake?: KkmHandshakeReady | null;
  /** Касса из привязки рабочего места: смена не открывается на чужой ККТ. */
  expectedKktDeviceId?: string | null;
}

/**
 * Ответы отправленных команд возвращаются наверх намеренно: в корне ответа `OpenShift`
 * KkmServer отдаёт `SessionNumber` открытой смены — прямое доказательство того, что документ
 * прошёл. Раньше результат выбрасывался, и подтверждение открытия оставалось зависеть от
 * `GetDataKKT`, который обновляет `SessionState` заметно позже.
 */
export interface FiscalShiftActionResult {
  handshake: KkmHandshakeReady;
  openShift: KkmExecutionResult | null;
}

export async function executeFiscalShiftAction(
  adapter: FiscalShiftAdapter,
  action: Exclude<FiscalShiftAction, null>,
  cashierName: string,
  cashierVatin?: string | null,
  options: FiscalShiftActionOptions = {},
): Promise<FiscalShiftActionResult> {
  const normalizedCashierName = cashierName.trim();

  if (normalizedCashierName === '') {
    throw new Error('В настройках кассы не указано, кто печатается в чеке. Попросите управляющего заполнить.');
  }

  const handshake = options.handshake ?? (await requireHandshakeReady(adapter, options.expectedKktDeviceId));

  const commandInput = {
    cashierName: normalizedCashierName,
    cashierVatIn: cashierVatin,
    deviceId: handshake.kktDevice.idDevice,
  };

  let openShift: KkmExecutionResult | null = null;

  if (action === 'close' || action === 'reopen') {
    requireCommandSuccess(
      await adapter.closeShift(commandInput),
      'Не получилось закрыть смену. Проверьте бумагу и попробуйте ещё раз.',
    );
  }

  if (action === 'open' || action === 'reopen') {
    openShift = requireCommandSuccess(
      await adapter.openShift(commandInput),
      'Не получилось открыть смену. Проверьте бумагу и попробуйте ещё раз.',
    );
  }

  return { handshake, openShift };
}

async function requireHandshakeReady(
  adapter: FiscalShiftAdapter,
  expectedKktDeviceId: string | null | undefined,
): Promise<KkmHandshakeReady> {
  const handshake = await adapter.handshake({ kktDeviceId: expectedKktDeviceId ?? undefined });

  if (handshake.kind !== 'ready') {
    throw new Error(handshake.instruction);
  }

  return handshake;
}

function requireCommandSuccess(result: KkmExecutionResult, message: string): KkmExecutionResult {
  if (result.kind !== 'ok') {
    throw new Error(message);
  }

  return result;
}

/** Доказательство открытой смены на ККТ: серийник ФН обязателен, номер смены — нет. */
export type KktOpeningProof = Omit<ConfirmShiftKktInput, 'shiftId'>;

/**
 * Доказательство из ответа самой команды `OpenShift`: `SessionNumber` в корне значит, что
 * касса провела документ открытия. Это надёжнее повторного `GetDataKKT` — 21.08 он отдавал
 * `SessionState = 1` ещё через четыре минуты после напечатанного документа ФД 622.
 */
export function proofFromShiftCommand(
  handshake: KkmHandshakeReady,
  result: KkmExecutionResult | null,
): KktOpeningProof | null {
  const fnSerial = handshake.kkt.fnNumber;
  const sessionNumber = result === null ? null : parseKkmShiftSessionNumber(result.response);

  if (fnSerial === null || sessionNumber === null) {
    return null;
  }

  return { fnSerial, kktShiftNumber: sessionNumber };
}

/** Доказательство из чтения состояния: касса сообщила открытую сессию. */
export function proofFromOpenSession(handshake: KkmHandshakeReady): KktOpeningProof | null {
  const fnSerial = handshake.kkt.fnNumber;

  if (handshake.kkt.sessionState !== 2 || fnSerial === null) {
    return null;
  }

  return { fnSerial, kktShiftNumber: handshake.kkt.sessionNumber };
}
